import { useBillingData } from "@sushicorp/contexts";
import { useCountries } from "@sushicorp/contexts";
import { fetchBillingDataSynchronized } from "@sushicorp/services";
import { FetchBillingDataSynchronizedResponse } from "@sushicorp/services";
import { useFetchBillingData } from "@sushicorp/services";
import { usePostBillingData } from "@sushicorp/services";
import { usePutBillingData } from "@sushicorp/services";
import { useFetchShippingAddresses } from "@sushicorp/services";
import { BaseBillingDataExtended } from "@sushicorp/types";
import { getMaxLength, trimFields } from "@sushicorp/utils";
import { events } from "artisn/analytics";
import { BillingData } from "artisn/types";
import { DocumentInput, DocumentTypeSelect, EmailInput } from "artisn-ui-react";
import { PhoneNumberInput, PhoneNumberInputValues } from "artisn-ui-react";
import { TextInput } from "artisn-ui-react";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useController, useFormContext } from "react-hook-form";
import { useQueryClient } from "react-query";

import Styles from "./BillingDataForm.styles";
import { BillingDataFormProps as Props } from "./BillingDataForm.types";
import { BillingDataFormValues } from "./BillingDataForm.types";
import Button from "components/global/Button/Button";
import Checkbox from "components/global/Checkbox/Checkbox";
import Switch from "components/global/Switch/Switch";
import TextInputWrapper from "components/global/TextInputWrapper/TextInputWrapper";
import useAuth from "contexts/auth/auth.context.hooks";
import useI18n from "hooks/useI18n";
import { notify } from "utils/common.utils";

const { logAddBillingInfo, logUpdateBillingInfo } = events.billing;

const BillingDataForm = forwardRef<HTMLFormElement, Props>((props, ref) => {
  const { method, billingData, onClose, hideButton, onBlur, resetData } = props;
  const { hideAddress, className, sourceForm = "billing" } = props;
  const { onDocumentTypeChange, withBillingDataSync = false } = props;
  const { showUserAddressesList = true, hideLastName = false } = props;
  const t = useI18n();
  const [useSavedAddress, setUseSavedAddress] = useState(false);
  const { countryISOCodes, selectedCountry } = useCountries();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { isAnonymous, uid } = auth;
  const { name: countryName, id: countryId, isoCode } = selectedCountry;
  const { code: countryCallingCode } = selectedCountry;
  const { isoCode: defaultCountryISOCode } = selectedCountry;
  const {
    mutate: postBillingData,
    reset: resetPost,
    isLoading: isLoadingPost
  } = usePostBillingData(auth);
  const {
    mutate: putBillingData,
    reset: resetPut,
    isLoading: isLoadingPut
  } = usePutBillingData(auth);
  const { data: shippingAddresses } = useFetchShippingAddresses(auth, notify);
  const { data: billingDataList } = useFetchBillingData(auth, notify);
  const billingDataFormContext = useFormContext<BillingDataFormValues>();
  const { watch, control, setError } = billingDataFormContext;
  const { resetField, setValue, reset: resetForm } = billingDataFormContext;
  const { handleSubmit, register, formState } = billingDataFormContext;
  const { getValues, clearErrors } = billingDataFormContext;
  const { formState: billingFormState } = billingDataFormContext;
  const { setValue: billingSetValue } = billingDataFormContext;
  const [phone, setPhone] = useState<PhoneNumberInputValues>({
    countryCallingCode: "",
    countryCode: isoCode,
    international: "",
    internationalRaw: "",
    isPossible: true,
    isValid: true,
    national: getValues().phone ?? "",
    nationalRaw: ""
  });
  const [isFetchingBillingData, setIsFetchingBillingData] = useState(false);
  const [controller, setController] = useState<AbortController | null>(null);
  const [phoneSwitch, setPhoneSwitch] = useState(true);
  const selectedDocumentType = watch("documentType");
  const { field } = useController({
    name: "phone",
    control,
    defaultValue: getValues().phone
  });
  // @ts-ignore
  const memCode = useRef<string | undefined>(billingData?.externalId);
  const documentInputStyle = selectedDocumentType ? "active" : "inactive";
  const { documentType: documentTypeError } = formState.errors ?? {};
  const { name, lastname } = formState.errors ?? {};
  const { document: documentError, phone: phoneError } = formState.errors ?? {};
  const { email } = formState.errors ?? {};
  const { address } = billingFormState.errors ?? {};
  const billingDataContext = useBillingData();
  const { setSelectedBillingData } = billingDataContext;
  const { setBillingFoundedModalConfig } = billingDataContext;
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * Function to import data.
   *
   * @since 1.0.0
   * @param {BillingData} billingDataSynchronized Billing data synchronized.
   */
  const onClickImportData = (
    billingDataSynchronized: FetchBillingDataSynchronizedResponse
  ) => {
    flushSync(() => {
      setPhoneSwitch(false);
    });

    setIsFetchingBillingData(false);
    const { name: billingDataSynchronizedName = "" } =
      billingDataSynchronized ?? {};
    const { lastname: billingDataSynchronizedLastname = "" } =
      billingDataSynchronized ?? {};
    const { phone: billingDataSynchronizedPhone } =
      billingDataSynchronized ?? {};
    const { number: billingDataSynchronizedNumber = "" } =
      billingDataSynchronizedPhone ?? {};
    const { email: billingDataSynchronizedEmail = "" } =
      billingDataSynchronized ?? {};
    const { externalId: billingDataSynchronizedMemCode = "" } =
      billingDataSynchronized ?? {};

    setValue(
      "name",
      `${billingDataSynchronizedName} ${billingDataSynchronizedLastname}`
    );
    const phone = billingDataSynchronizedNumber.slice(1);
    setValue("lastname", billingDataSynchronizedLastname);
    setValue("phone", phone);
    setValue("email", billingDataSynchronizedEmail);
    setValue("address", "");
    setValue("externalId", billingDataSynchronizedMemCode);

    memCode.current = billingDataSynchronizedMemCode;

    flushSync(() => {
      setPhone({
        countryCallingCode,
        countryCode: defaultCountryISOCode,
        international: "",
        internationalRaw: "",
        isPossible: false,
        isValid: true,
        national: phone,
        nationalRaw: ""
      });
    });

    field.onChange(phone);

    flushSync(() => {
      setPhoneSwitch(true);
    });

    onCloseBillingDataFoundedModal(false);
  };

  const resetFields = () => {
    resetField("name");
    resetField("lastname");
    resetField("phone");
    flushSync(() => {
      setPhoneSwitch(false);
    });
    flushSync(() => {
      setPhone({
        countryCallingCode,
        countryCode: defaultCountryISOCode,
        international: "",
        internationalRaw: "",
        isPossible: false,
        isValid: true,
        national: "",
        nationalRaw: ""
      });
    });
    flushSync(() => {
      setPhoneSwitch(true);
    });
    resetField("email");
    resetField("address");
  };

  /**
   * Function to close modal
   *
   * @since 1.0.0
   */
  const onCloseBillingDataFoundedModal = (withResetFields = true) => {
    if (withResetFields) {
      resetFields();
      // @ts-ignore
      setValue("externalId", billingData?.externalId);
      // @ts-ignore
      memCode.current = billingData?.externalId;
    }
    setIsFetchingBillingData(false);
    setBillingFoundedModalConfig({
      show: false,
      foundBillingDataName: "",
      onClickImportData: undefined,
      onClose: undefined
    });
  };

  /**
   * Handler for when the user enters their document number.
   *
   * @since 1.0.0
   */
  const documentChangeHandler = async () => {
    try {
      if (!withBillingDataSync) return;
      if (getValues().document.length < 3) return;

      setIsFetchingBillingData(true);
      setTimeout(() => {
        setIsFetchingBillingData(false);
      }, 2000);

      const currentController = new AbortController();

      if (controller) controller.abort();

      setController(currentController);

      const billingDataSynchronized = await fetchBillingDataSynchronized(
        {
          documentType: getValues().documentType,
          document: getValues().document
        },
        currentController
      );
      if (!shippingAddresses?.length) {
        queryClient.invalidateQueries([uid, "addresses"]);
      }
      if (Object.keys(billingDataSynchronized).length === 0) {
        resetFields();
        setValue("externalId", undefined);
        memCode.current = undefined;
        return;
      }

      const { name: billingDataSynchronizedName = "" } =
        billingDataSynchronized ?? {};
      setBillingFoundedModalConfig({
        show: true,
        foundBillingDataName: billingDataSynchronizedName,
        onClickImportData: () => onClickImportData(billingDataSynchronized),
        onClose: onCloseBillingDataFoundedModal
      });
    } catch (error) {
      resetFields();
      setValue("externalId", undefined);
      memCode.current = undefined;
    }
  };

  const switchHandler = () => {
    billingSetValue("address", "");
    setUseSavedAddress(prevState => !prevState);
  };

  const createBillingData = (form: BillingDataFormValues) => {
    const newBillingData: BaseBillingDataExtended = {
      address: form.address,
      country: {
        name: countryName,
        id: countryId
      },
      default: form.default,
      document: form.document,
      documentType: form.documentType,
      email: form.email,
      name: `${form.name}`,
      lastname: form.lastname,
      nickname: "",
      phone: form.phone,
      //@ts-ignore
      externalId: memCode.current ?? null,
      additionalInfo: {
        lastName: form.lastname
      }
    };
    postBillingData(newBillingData, {
      onError: e => {
        setErrorMessage(
          e.message || "Ocurrió un problema al guardar el dato de facturación"
        );
        console.error("BillingDataForm: An error occurred in the POST request");
      },
      onSuccess: () => {
        logAddBillingInfo({ alias: form.name });
        resetPost();
        resetForm();
        memCode.current = undefined;
        onClose?.();
      }
    });
  };

  const updateBillingData = (form: BillingDataFormValues) => {
    if (!billingData) {
      console.error(`BillingDataForm: If you want to update a billing data, consider
      passing a BillingData object to prevent some data from being reset.`);
    }
    if (!billingData) return;

    const updatedBillingData: BillingData = {
      ...billingData,
      address: form?.address ?? "",
      country: {
        id: countryId,
        name: billingData?.country.name ?? ""
      },
      default:
        billingDataList && billingDataList.length > 0 && !billingData.default
          ? false
          : true,
      document: form.document ?? "",
      documentType: form.documentType,
      email: form.email ?? "",
      id: billingData.id,
      name: form.name ?? "",
      nickname: billingData?.nickname ?? "",
      phone: form.phone ?? "",
      //@ts-ignore
      externalId: memCode.current ?? null,
      additionalInfo: {
        lastName: form.lastname ?? ""
      }
    };
    putBillingData(updatedBillingData, {
      onError: e => {
        setErrorMessage(
          e.message ??
            "Ocurrió un problema al actualizar el dato de facturación"
        );
        console.error("BillingDataForm: An error occurred in the PUT request");
      },
      onSuccess: billingData => {
        logUpdateBillingInfo({
          alias: form.name,
          fields: Object.keys(formState.touchedFields)
        });
        const { default: isDefault } = billingData ?? {};
        if (isDefault) setSelectedBillingData(billingData);
        resetPut();
        resetForm();
        memCode.current = undefined;
        onClose?.();
      }
    });
  };

  const submitHandler = (form: BillingDataFormValues) => {
    const newForm = trimFields({ ...form, document: `${form.document}` });

    setErrorMessage("");
    if (method === "POST") createBillingData(newForm);
    if (method === "PUT") updateBillingData(newForm);
  };

  const renderAddress = () => {
    if (hideAddress) return null;
    if (!useSavedAddress) {
      return (
        <TextInputWrapper
          className="BillingDataForm__field"
          showSpinner={isFetchingBillingData}
        >
          <TextInput
            {...register("address")}
            className="field"
            label={t.common.forms.label.address}
            placeholder={t.common.forms.placeholder.address}
            errorMessage={address?.message}
            maxLength={50}
          />
        </TextInputWrapper>
      );
    }
    return (
      <div className="BillingDataForm__address-select-container">
        <select
          {...register("address")}
          className="BillingDataForm__address-select"
        >
          <option value="">{t.common.forms.label.selectAnAddress}</option>
          {shippingAddresses?.map(address => {
            const { id, mainStreet, secondaryStreet, reference } = address;
            const { nickname } = address;
            return (
              <option
                key={id}
                value={`${mainStreet}, ${secondaryStreet}, ${reference}`}
              >
                {nickname}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  useEffect(() => {
    onDocumentTypeChange?.(selectedDocumentType);

    if (selectedDocumentType === "RUC") {
      setValue("lastname", "");
    }
  }, [onDocumentTypeChange, selectedDocumentType, setValue]);

  useEffect(() => {
    if (!billingData || method !== "PUT") return;
    setValue("address", billingData?.address);
  }, [method, billingData, setValue]);

  useEffect(() => {
    if (!resetData) return;
    resetForm();
  }, [resetData, resetForm]);

  return (
    <Styles
      ref={ref}
      className={`BillingDataForm ${className}`}
      onSubmit={handleSubmit(submitHandler)}
      autoComplete="off"
    >
      <DocumentTypeSelect
        {...register("documentType", {
          onChange: () => {
            resetField("document", { defaultValue: "" });
            clearErrors("document");
          }
        })}
        className="field"
        label={t.common.forms.label.documentType}
        countryCode={isoCode}
        errorMessage={documentTypeError?.message}
        onBlur={onBlur}
        id={`${sourceForm}-documentType`}
      />
      <DocumentInput
        {...register("document", {
          onChange: () => documentChangeHandler()
        })}
        className={`field ${documentInputStyle}`}
        label={t.common.forms.label.documentNumber}
        placeholder={t.common.forms.placeholder.documentNumber}
        countryCode={isoCode}
        documentType={getValues().documentType ?? "PASSPORT"}
        errorMessage={documentError?.message}
        onBlur={onBlur}
        id={`${sourceForm}-document`}
        disabled={!selectedDocumentType}
        maxLength={getMaxLength(getValues().documentType)}
      />
      <TextInputWrapper
        className="BillingDataForm__field"
        showSpinner={isFetchingBillingData}
      >
        <TextInput
          {...register("name")}
          className="field"
          label={
            selectedDocumentType === "RUC"
              ? t.common.forms.label.businessName
              : hideLastName
              ? `${t.common.forms.label.name} ${t.common.and} ${t.common.forms.label.lastName}`
              : t.common.forms.label.name
          }
          placeholder={
            selectedDocumentType === "RUC"
              ? t.common.forms.label.businessName
              : t.common.forms.label.name
          }
          errorMessage={name?.message}
          onBlur={onBlur}
          id={`${sourceForm}-name`}
          maxLength={50}
        />
      </TextInputWrapper>
      {!hideLastName && selectedDocumentType !== "RUC" ? (
        <TextInputWrapper
          className="BillingDataForm__field"
          showSpinner={isFetchingBillingData}
        >
          <TextInput
            {...register("lastname")}
            className="field"
            label={t.common.forms.label.lastName}
            placeholder={t.common.forms.label.lastName}
            errorMessage={lastname?.message}
            onBlur={onBlur}
            id={`${sourceForm}-lastname`}
            maxLength={50}
          />
        </TextInputWrapper>
      ) : null}
      <TextInputWrapper
        className="BillingDataForm__field"
        showSpinner={isFetchingBillingData}
      >
        <EmailInput
          {...register("email")}
          className="field"
          label={t.common.forms.label.email}
          placeholder={t.common.forms.placeholder.email}
          errorMessage={email?.message}
          onBlur={onBlur}
          id={`${sourceForm}-email`}
          maxLength={50}
        />
      </TextInputWrapper>
      {phoneSwitch ? (
        <TextInputWrapper
          className="BillingDataForm__field"
          showSpinner={isFetchingBillingData}
        >
          <PhoneNumberInput
            {...field}
            className="field BillingDataForm__phone-input"
            label={t.common.forms.label.phone}
            countriesCodes={countryISOCodes}
            onChange={e => {
              setPhone(e);
              field.onChange(e.national);
              if (!e.national.startsWith("9")) {
                setPhone({ ...e, isValid: false });
                setError("phone", { message: t.profile.user.errors.phone });
              }
            }}
            placeholder={t.common.forms.placeholder.phone}
            errorMessage={phoneError?.message}
            inputValue={phone.national}
            onBlur={onBlur}
            id={`${sourceForm}-phone`}
            maxLength={11}
          />
        </TextInputWrapper>
      ) : null}

      {!hideAddress && !isAnonymous && showUserAddressesList ? (
        <div className="BillingDataForm__use-saved-address">
          <p className="BillingDataForm__use-saved-address__text">
            {t.common.forms.label.useSaveDirection}
          </p>
          <Switch
            name="billing-data-form-switch"
            checked={useSavedAddress}
            onChange={switchHandler}
          />
        </div>
      ) : null}
      {renderAddress()}
      {method === "POST" && billingDataList?.length !== 0 ? (
        <Checkbox
          {...register("default")}
          label={t.common.forms.label.useLikeDefault}
          name="default"
          defaultChecked={getValues().default}
          className="BillingDataForm__checkbox field"
        />
      ) : null}
      <p className="BillingDataForm__error-message">{errorMessage}</p>
      {!hideButton ? (
        <Button
          className="BillingDataForm__submit-button"
          isLoading={isLoadingPost || isLoadingPut}
          htmlType="submit"
        >
          {t.common.saveBillingData}
        </Button>
      ) : null}
    </Styles>
  );
});

BillingDataForm.defaultProps = {
  className: ""
};
BillingDataForm.displayName = "BillingDataForm";

export default BillingDataForm;
